import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { ErrorState } from '@/components/ui/ErrorState'
import { Input } from '@/components/ui/Input'
import { LoadingState } from '@/components/ui/LoadingState'
import { PageHeader } from '@/components/ui/PageHeader'
import { Textarea } from '@/components/ui/Textarea'
import { PromotionEstimateCard } from '@/components/venue/PromotionEstimateCard'
import { PromotionMediaFields } from '@/components/venue/PromotionMediaFields'
import { PromotionSendConfirmModal } from '@/components/venue/PromotionSendConfirmModal'
import { PromotionTargetPicker } from '@/components/venue/PromotionTargetPicker'
import { useCampaignDetail } from '@/hooks/useCampaignDetail'
import { useCampaignEstimate } from '@/hooks/useCampaignEstimate'
import { useCreateCampaign } from '@/hooks/useCreateCampaign'
import { useUpdateCampaign } from '@/hooks/useUpdateCampaign'
import { toApiError, type ApiError } from '@/lib/api/errors'
import { cn } from '@/lib/utils/cn'
import {
  changePromotionRegionState,
  hasPromotionComposeFormErrors,
  initialPromotionComposeFormState,
  nowWallClockKst,
  promotionDetailToFormState,
  promotionEstimateParamsReady,
  toPromotionCreateInput,
  toPromotionEstimateParams,
  toPromotionUpdateInput,
  validatePromotionComposeForm,
  type PromotionComposeFormErrors,
  type PromotionComposeFormState,
} from '@/lib/venue/promotionCampaignViewModel'
import { useVenueContext } from '@/routes/venueContext'

const TITLE_MAX_LENGTH = 100
const BODY_MAX_LENGTH = 1000

/** "임시 저장" 버튼용 — 발송 모드·예약 시각 검증은 건너뛰고 필수 필드만 확인한다(제목·본문·지역). */
function validateDraftForm(form: PromotionComposeFormState): PromotionComposeFormErrors {
  return validatePromotionComposeForm({ ...form, sendMode: 'IMMEDIATE' }, nowWallClockKst())
}

function FieldError({ message }: { message: string | undefined }) {
  if (!message) return null
  return <p className="mt-1 text-xs text-danger-500">{message}</p>
}

const SEND_MODE_OPTIONS: { value: PromotionComposeFormState['sendMode']; label: string }[] = [
  { value: 'IMMEDIATE', label: '즉시 발송' },
  { value: 'SCHEDULED', label: '예약 발송' },
]

/**
 * 지역 홍보 작성/이어서 작성 — 'notices/promotions/new'·'notices/promotions/:campaignId/edit' 겸용
 * (설계 §2). `:campaignId`가 있으면 기존 DRAFT를 불러와 프리필한다(대상 조건은 읽기 전용).
 *
 * 🚨 이 화면은 조립만 한다. 검증·입력 변환은 `promotionCampaignViewModel.ts`가 소유한다.
 */
export function VenuePromotionComposePage() {
  const { venueId } = useVenueContext()
  const navigate = useNavigate()
  const params = useParams<{ campaignId?: string }>()
  const editingCampaignId = params.campaignId ? Number(params.campaignId) : null
  const isEditMode = editingCampaignId !== null

  const detailQuery = useCampaignDetail(venueId, editingCampaignId ?? NaN)

  const [form, setForm] = useState<PromotionComposeFormState>(initialPromotionComposeFormState)
  const [errors, setErrors] = useState<PromotionComposeFormErrors>({})
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [prefilled, setPrefilled] = useState(false)
  const [savedCampaignId, setSavedCampaignId] = useState<number | null>(editingCampaignId)
  const [draftSavedMessage, setDraftSavedMessage] = useState<string | null>(null)
  const [draftError, setDraftError] = useState<ApiError | null>(null)

  const createMutation = useCreateCampaign(venueId)
  const updateMutation = useUpdateCampaign(venueId)

  useEffect(() => {
    if (isEditMode && detailQuery.data && !prefilled) {
      setForm(promotionDetailToFormState(detailQuery.data))
      setPrefilled(true)
    }
  }, [isEditMode, detailQuery.data, prefilled])

  const estimateQuery = useCampaignEstimate(
    venueId,
    toPromotionEstimateParams(form),
    promotionEstimateParamsReady(form)
  )

  function updateField<K extends keyof PromotionComposeFormState>(key: K, value: PromotionComposeFormState[K]) {
    setForm((current) => ({ ...current, [key]: value }))
  }

  async function handleSaveDraft() {
    const draftErrors = validateDraftForm(form)
    if (hasPromotionComposeFormErrors(draftErrors)) {
      setErrors(draftErrors)
      return
    }
    setErrors({})
    setDraftError(null)
    setDraftSavedMessage(null)
    try {
      if (savedCampaignId === null) {
        const created = await createMutation.mutateAsync(toPromotionCreateInput(form, 'DRAFT'))
        setSavedCampaignId(created.summary.id)
        navigate(`/venues/${venueId}/notices/promotions/${created.summary.id}/edit`, { replace: true })
      } else {
        await updateMutation.mutateAsync({ campaignId: savedCampaignId, input: toPromotionUpdateInput(form) })
      }
      setDraftSavedMessage('임시 저장했습니다. 나중에 이어서 작성할 수 있습니다.')
    } catch (error) {
      setDraftError(toApiError(error))
    }
  }

  function handleSubmit() {
    const validationErrors = validatePromotionComposeForm(form, nowWallClockKst())
    if (hasPromotionComposeFormErrors(validationErrors)) {
      setErrors(validationErrors)
      return
    }
    setErrors({})
    setConfirmOpen(true)
  }

  if (isEditMode && detailQuery.isLoading) {
    return <LoadingState rows={4} label="임시 저장된 홍보를 불러오는 중" />
  }

  if (isEditMode && detailQuery.isError) {
    return (
      <ErrorState
        error={detailQuery.error}
        onRetry={() => {
          void detailQuery.refetch()
        }}
      />
    )
  }

  if (isEditMode && detailQuery.data && !detailQuery.data.summary.editable) {
    return (
      <Alert tone="danger" title="이어서 작성할 수 없습니다">
        <p>이미 발송이 진행됐거나 완료된 홍보는 내용을 수정할 수 없습니다.</p>
      </Alert>
    )
  }

  return (
    <>
      <PageHeader
        title={isEditMode ? '지역 홍보 이어서 작성' : '지역 홍보 작성'}
        description="광고 수신에 동의한 지역 사용자에게 발송하는 유료 홍보입니다. 실제 발송 건수를 기준으로 비용이 부과됩니다."
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader title="발송 대상" />
          <CardBody>
            <PromotionTargetPicker
              form={form}
              errors={errors}
              readOnly={isEditMode}
              onChangeRegionState={(regionStateId) => {
                setForm((current) => changePromotionRegionState(current, regionStateId))
              }}
              onChangeRegionCity={(regionCityId) => {
                updateField('regionCityId', regionCityId)
              }}
              onChangeSendSlot={(sendSlot) => {
                updateField('sendSlot', sendSlot)
              }}
            />
          </CardBody>
        </Card>

        <PromotionEstimateCard
          estimate={estimateQuery.data}
          isLoading={estimateQuery.isLoading}
          isError={estimateQuery.isError}
          errorMessage={estimateQuery.error ? toApiError(estimateQuery.error).message : undefined}
        />

        <Card>
          <CardHeader title="내용 작성" />
          <CardBody className="space-y-4">
            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <label className="text-sm font-medium text-ink-700" htmlFor="promotion-title">
                  제목
                </label>
                <span className="text-xs text-slateish-400">
                  {form.title.length}/{TITLE_MAX_LENGTH}
                </span>
              </div>
              <Input
                id="promotion-title"
                value={form.title}
                maxLength={TITLE_MAX_LENGTH}
                aria-invalid={Boolean(errors.title)}
                placeholder="예) 여름밤 심야 코트 20% 할인"
                onChange={(event) => {
                  updateField('title', event.target.value)
                }}
              />
              <FieldError message={errors.title} />
            </div>

            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <label className="text-sm font-medium text-ink-700" htmlFor="promotion-body">
                  내용
                </label>
                <span className="text-xs text-slateish-400">
                  {form.body.length}/{BODY_MAX_LENGTH}
                </span>
              </div>
              <Textarea
                id="promotion-body"
                rows={5}
                value={form.body}
                maxLength={BODY_MAX_LENGTH}
                aria-invalid={Boolean(errors.body)}
                placeholder="예) 8월 한 달간 22시 이후 코트 이용료를 20% 할인합니다."
                onChange={(event) => {
                  updateField('body', event.target.value)
                }}
              />
              <FieldError message={errors.body} />
            </div>

            <PromotionMediaFields
              venueId={venueId}
              imageUrl={form.imageUrl}
              linkUrl={form.linkUrl}
              linkUrlError={errors.linkUrl}
              onChangeImageUrl={(imageUrl) => {
                updateField('imageUrl', imageUrl)
              }}
              onChangeLinkUrl={(linkUrl) => {
                updateField('linkUrl', linkUrl)
              }}
            />
          </CardBody>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader title="미리보기" description="실제 앱 표시와 다를 수 있습니다." />
            <CardBody>
              <div className="rounded-lg border border-slateish-200 p-3">
                {form.imageUrl ? (
                  <img src={form.imageUrl} alt="" className="mb-2 h-32 w-full rounded object-cover" />
                ) : null}
                <p className="text-sm font-semibold text-ink-700">{form.title || '제목을 입력해 주세요'}</p>
                <p className="mt-1 whitespace-pre-wrap text-sm text-slateish-600">
                  {form.body || '내용을 입력해 주세요'}
                </p>
              </div>
              <p className="mt-2 text-xs text-slateish-500">
                수신자가 발송물을 통해 이 구장의 상세 화면으로 연결됩니다.
              </p>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="발송 일시" />
            <CardBody className="space-y-3">
              <div role="tablist" aria-label="발송 일시" className="flex gap-2">
                {SEND_MODE_OPTIONS.map((option) => {
                  const selected = form.sendMode === option.value
                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="tab"
                      aria-selected={selected}
                      onClick={() => {
                        updateField('sendMode', option.value)
                      }}
                      className={cn(
                        'rounded-full border px-3 py-1.5 text-sm font-medium transition-colors',
                        selected
                          ? 'border-brand-500 bg-brand-50 text-brand-500'
                          : 'border-slateish-300 text-slateish-600 hover:bg-slateish-50'
                      )}
                    >
                      {option.label}
                    </button>
                  )
                })}
              </div>

              {form.sendMode === 'SCHEDULED' ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="promotion-scheduled-date">
                      예약 날짜
                    </label>
                    <Input
                      id="promotion-scheduled-date"
                      type="date"
                      value={form.scheduledDate}
                      aria-invalid={Boolean(errors.scheduledAt)}
                      onChange={(event) => {
                        updateField('scheduledDate', event.target.value)
                      }}
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="promotion-scheduled-time">
                      예약 시각
                    </label>
                    <Input
                      id="promotion-scheduled-time"
                      type="time"
                      value={form.scheduledTime}
                      aria-invalid={Boolean(errors.scheduledAt)}
                      onChange={(event) => {
                        updateField('scheduledTime', event.target.value)
                      }}
                    />
                  </div>
                  <div className="col-span-2">
                    <FieldError message={errors.scheduledAt} />
                  </div>
                </div>
              ) : null}
            </CardBody>
          </Card>

          {draftSavedMessage ? (
            <Alert tone="info">
              <p>{draftSavedMessage}</p>
            </Alert>
          ) : null}
          {draftError ? (
            <Alert tone="danger">
              <p>{draftError.message}</p>
            </Alert>
          ) : null}

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              disabled={createMutation.isPending || updateMutation.isPending}
              onClick={() => {
                void handleSaveDraft()
              }}
            >
              임시 저장
            </Button>
            <Button type="button" onClick={handleSubmit}>
              발송
            </Button>
          </div>
        </div>
      </div>

      {confirmOpen ? (
        <PromotionSendConfirmModal
          venueId={venueId}
          form={form}
          campaignId={savedCampaignId}
          onClose={() => {
            setConfirmOpen(false)
          }}
          onSent={(campaignId) => {
            navigate(`/venues/${venueId}/notices/${campaignId}`)
          }}
          onScheduled={(campaignId) => {
            navigate(`/venues/${venueId}/notices/${campaignId}`)
          }}
        />
      ) : null}
    </>
  )
}
